/** Registration page variants — endpoints, field keys, layout, and Phase 8 flow metadata. */

/** @typedef {'professionalGuardian'|'lawFirmUser'|'internalUser'|'teamMember'|'professionalGuardianInvite'|'pgContact'} RegisterVariantId */

/**
 * Duplicate-email alert placement matches legacy JSX order.
 * - afterEmail: alert immediately after email field
 * - beforeSubmit: alert after password fields (public PG)
 * - none: no duplicate check UI
 */

/** @type {Record<RegisterVariantId, object>} */
export const REGISTER_VARIANTS = Object.freeze({
  professionalGuardian: {
    id: "professionalGuardian",
    title: "Professional Guardian Registration",
    headlineTitle: "Professional Guardian Registration",
    headlineSubtitle: "Create your professional guardian account.",
    emailField: "email_address",
    useToken: false,
    registerPath: "/register-professional-guardian",
    successNavigate: "/",
    successMessage: "Thank you for registering. Please login to continue.",
    httpClient: "apiClient",
    minHeight: undefined,
    duplicateEmailPlacement: "beforeSubmit",
    useDuplicateEmailCheck: true,
    showInternalInfoBanner: false,
    confirmPasswordShareVisibility: false,
    submitButtonLabel: "Create Account",
    backButtonPath: "/",
    fieldOrder: ["email_address", "username", "password", "confirmPassword"],
    validateAllToast: "Please fill in all required fields correctly.",
    skipPreventDefaultOnSubmit: false,
    hints: {
      email_address: "Enter your email address",
      username: "Enter a unique username for your account",
    },
  },
  lawFirmUser: {
    id: "lawFirmUser",
    title: "Law Firm User Registration",
    headlineTitle: "Law Firm Registration",
    headlineSubtitle: "Create your law firm account",
    emailField: "email",
    useToken: true,
    registerPath: null,
    submitPath: (token) => `/lawfirm-user-register/${token}`,
    httpClient: "apiClientRaw",
    minHeight: "100vh",
    duplicateEmailPlacement: "afterEmail",
    useDuplicateEmailCheck: true,
    showInternalInfoBanner: false,
    confirmPasswordShareVisibility: false,
    submitButtonLabel: "Register",
    backButtonPath: "/login",
    showSecondaryNavButton: false,
    fieldOrder: ["email", "username", "password", "confirmPassword"],
    validateAllToast: null,
    usernameMinLength: null,
    hints: {
      email: "Enter your email address",
      username: "Enter a unique username (cannot be an email address)",
    },
  },
  internalUser: {
    id: "internalUser",
    title: "Internal User Registration",
    headlineTitle: "Create Internal User Account",
    headlineSubtitle: "Please fill out the form to register",
    emailField: "email",
    useToken: true,
    submitPath: (token) => `/register-internal-user/${token}`,
    httpClient: "apiClientRaw",
    minHeight: "100vh",
    duplicateEmailPlacement: "none",
    useDuplicateEmailCheck: false,
    showInternalInfoBanner: true,
    confirmPasswordShareVisibility: false,
    submitButtonLabel: "REGISTER",
    backButtonPath: "/login",
    fieldOrder: ["username", "name", "password", "confirmPassword"],
    validateAllToast: null,
    usernameMinLength: null,
    hints: {
      username: "Enter a unique username (cannot be an email address)",
      name: "Enter your full legal name",
      password:
        "Enter a strong password with at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character",
      confirmPassword: "Confirm your password by entering it again",
    },
  },
  teamMember: {
    id: "teamMember",
    title: "Team Member Registration",
    headlineTitle: "Create Team Member Account",
    headlineSubtitle: "Please fill out the form to create your account",
    emailField: "email",
    useToken: true,
    submitPath: (token) => `/carrier-team-member/create/${token}`,
    httpClient: "apiClientRaw",
    minHeight: "100vh",
    duplicateEmailPlacement: "afterEmail",
    useDuplicateEmailCheck: true,
    showInternalInfoBanner: false,
    confirmPasswordShareVisibility: false,
    submitButtonLabel: "REGISTER",
    backButtonPath: "/login",
    fieldOrder: ["username", "email", "name", "phonenumber", "password", "confirmPassword"],
    validateAllToast: null,
    usernameMinLength: null,
    hints: {
      username: "Enter a unique username for your account",
      email: "Enter your email address",
      name: "Enter your full name",
      phonenumber: "Enter your phone number",
    },
  },
  pgContact: {
    id: "pgContact",
    title: "Professional Guardian Contact Registration",
    headlineTitle: "Professional Guardian Contact Registration",
    headlineSubtitle: "Complete your registration using your invite token",
    emailField: "email",
    useToken: true,
    httpClient: "apiClient",
    profileCompletionPath: (token) => `/pg-contacts/profile-completion/${token}`,
    submitPathStatic: "/pg-contacts-register/",
    minHeight: "100vh",
    duplicateEmailPlacement: "afterEmail",
    useDuplicateEmailCheck: true,
    showInternalInfoBanner: false,
    confirmPasswordShareVisibility: true,
    submitButtonLabel: "REGISTER",
    backButtonPath: "/login",
    fieldOrder: ["username", "email", "password", "confirmPassword"],
    validateAllToast: null,
    usernameMinLength: null,
    hints: {
      username: "Enter a unique username (cannot be an email address)",
      email: "Enter your email address",
      password:
        "Enter a strong password with at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character",
      confirmPassword: "Re-enter your password to confirm",
    },
  },
  professionalGuardianInvite: {
    id: "professionalGuardianInvite",
    title: "Professional Guardian Registration",
    headlineTitle: "Professional Guardian Registration",
    headlineSubtitle: "Create your professional guardian account.",
    emailField: "email_address",
    useToken: true,
    submitPath: (token) => `/register-by-invite/${token}/`,
    httpClient: "apiClientRaw",
    minHeight: "100vh",
    duplicateEmailPlacement: "afterEmail",
    useDuplicateEmailCheck: true,
    showInternalInfoBanner: false,
    confirmPasswordShareVisibility: false,
    submitButtonLabel: "Create Account",
    backButtonPath: "/",
    showSecondaryNavButton: true,
    fieldOrder: ["email_address", "username", "password", "confirmPassword"],
    validateAllToast: null,
    usernameMinLength: 3,
    hints: {
      email_address:
        "Enter your email address for account verification and communication",
      username: "Choose a unique username for your account (cannot be an email address)",
    },
  },
});

/**
 * @param {RegisterVariantId} id
 */
export function getRegisterVariant(id) {
  return REGISTER_VARIANTS[id] ?? REGISTER_VARIANTS.professionalGuardian;
}

/** Initial form slices per variant (omit keys not rendered). */
export function getInitialFormData(variantId) {
  switch (variantId) {
    case "professionalGuardian":
    case "professionalGuardianInvite":
      return {
        email_address: "",
        username: "",
        password: "",
        confirmPassword: "",
      };
    case "lawFirmUser":
      return { email: "", username: "", password: "", confirmPassword: "" };
    case "internalUser":
      return { name: "", username: "", password: "", confirmPassword: "" };
    case "teamMember":
      return {
        email: "",
        name: "",
        username: "",
        phonenumber: "",
        password: "",
        confirmPassword: "",
      };
    case "pgContact":
      return {
        token: "",
        username: "",
        password: "",
        confirmPassword: "",
        email: "",
        phone_number: "",
      };
    default:
      return {};
  }
}
